import type { ScopedThreadRef } from "@amoreg/contracts";
import { scopedThreadKey } from "@amoreg/client-runtime/environment";
import {
  HTML_PAGE_PLACEHOLDER_HEIGHT,
  htmlPageInlineUrl,
  htmlPageResult,
  htmlPageThemeMessage,
  type HtmlPageTheme,
} from "@amoreg/shared/htmlPage";
import { PanelRightOpenIcon, PictureInPicture2Icon, RotateCwIcon } from "lucide-react";
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useAssetUrlRefresh } from "../../assets/assetUrls";
import { useFloatingFigureStore } from "../../floatingFigureStore";
import { useRightPanelStore } from "../../rightPanelStore";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { FigureActionButton } from "./FigureActionButton";
import { FigureHostContext } from "./figureHost";
import { FLOATING_FIGURE_DEFAULT_WIDTH } from "./floatingFigureLayout";
import { useHtmlPageTheme } from "./htmlPageTheme";
import {
  floatingHtmlPageId,
  htmlPageTitle,
  initialHtmlPageLoad,
  isHtmlPagePreparing,
  nextHtmlPageLoad,
  readHtmlPageFrameMessage,
} from "./htmlPageLoad";

/**
 * Mints a URL for a workspace page; `null` (or a rejection) when the file is
 * not there (yet). The default asks the server for a signed asset URL; the
 * browser fixture provides its own.
 */
export type WorkspacePageMint = (
  input: { readonly threadRef: ScopedThreadRef; readonly path: string },
  signal: AbortSignal,
) => Promise<string | null>;

export const WorkspacePageMintContext = createContext<WorkspacePageMint | null>(null);

function useWorkspacePageMint(threadRef: ScopedThreadRef, path: string): WorkspacePageMint {
  const override = use(WorkspacePageMintContext);
  const resource = useMemo(
    () => ({ _tag: "workspace-file" as const, threadId: threadRef.threadId, path }),
    [path, threadRef.threadId],
  );
  // A fresh mint every time: a cached failure from a turn whose sync had not
  // landed yet must not stick.
  const refresh = useAssetUrlRefresh(threadRef.environmentId, resource);
  return useCallback<WorkspacePageMint>(
    (input, signal) => (override ? override(input, signal) : refresh(signal)),
    [override, refresh],
  );
}

/**
 * An agent's interactive HTML page inline in a reply: `![Title](pages/x.html)`
 * whose file lives in the thread's workspace. The page runs in a sandboxed,
 * opaque-origin frame served by the server under a strict policy
 * (`@amoreg/shared/htmlPage`); it wears the app theme, is as tall as its
 * content (so it never takes the thread's scroll), and asks this host to open
 * links. While the reply streams, and until the turn's workspace sync lands,
 * the page says it is on its way.
 */
export function WorkspaceHtmlPage({
  threadRef,
  path,
  panelPath,
  alt,
  isStreaming,
}: {
  readonly threadRef: ScopedThreadRef;
  /** The path the asset is minted for (workspace-absolute or relative). */
  readonly path: string;
  /** The workspace-relative path the Files panel opens, when known. */
  readonly panelPath: string | null;
  readonly alt: string | undefined;
  readonly isStreaming: boolean;
}) {
  const host = use(FigureHostContext);
  const floating = host === "floating";
  const title = htmlPageTitle(alt, path);
  const figureRef = useRef<HTMLElement | null>(null);
  const theme = useHtmlPageTheme(figureRef);
  const [load, dispatch] = useReducer(nextHtmlPageLoad, isStreaming, initialHtmlPageLoad);
  const [navigatedAway, setNavigatedAway] = useState(false);
  const mint = useWorkspacePageMint(threadRef, path);

  useEffect(() => {
    dispatch({ type: "streaming", streaming: isStreaming });
  }, [isStreaming]);

  const mintingAttempt = load.phase === "minting" ? load.attempt : null;
  useEffect(() => {
    if (mintingAttempt === null) return;
    const controller = new AbortController();
    mint({ threadRef, path }, controller.signal).then(
      (url) => {
        if (!controller.signal.aborted)
          dispatch(url ? { type: "minted", url } : { type: "failed" });
      },
      () => {
        if (!controller.signal.aborted) dispatch({ type: "failed" });
      },
    );
    return () => controller.abort();
  }, [mint, mintingAttempt, path, threadRef]);

  const retryDelay = load.phase === "retrying" ? load.delayMs : null;
  const retryAttempt = load.phase === "retrying" ? load.attempt : null;
  useEffect(() => {
    if (retryDelay === null) return;
    const timer = setTimeout(() => dispatch({ type: "retry-due" }), retryDelay);
    return () => clearTimeout(timer);
  }, [retryDelay, retryAttempt]);

  const floatingId = floatingHtmlPageId(scopedThreadKey(threadRef), path);
  const isFloating = useFloatingFigureStore((state) => state.figure?.id === floatingId);
  const ready = load.phase === "ready" && !navigatedAway;

  return (
    <figure
      ref={figureRef}
      className="chat-markdown-diagram chat-markdown-page"
      data-html-page={path}
      data-fill={floating ? "true" : undefined}
      aria-label={title}
    >
      {load.phase === "ready" && navigatedAway ? (
        <PageNotice title="Page closed" path={path}>
          It tried to navigate away from the reply.
        </PageNotice>
      ) : load.phase === "ready" && theme ? (
        <HtmlPageFrame
          url={load.url}
          theme={theme}
          title={title}
          fill={floating}
          onNavigatedAway={() => setNavigatedAway(true)}
        />
      ) : load.phase === "unavailable" ? (
        <PageNotice
          title="Page unavailable"
          path={path}
          action={
            <Button size="xs" variant="outline" onClick={() => dispatch({ type: "retry" })}>
              <RotateCwIcon />
              Try again
            </Button>
          }
        />
      ) : (
        <PagePreparing fill={floating} label={isHtmlPagePreparing(load) ? "Preparing page…" : ""} />
      )}
      {ready && !floating ? (
        <span
          className="chat-markdown-diagram-actions"
          data-figure-actions=""
          role="toolbar"
          aria-label="Page actions"
        >
          {panelPath ? (
            <FigureActionButton
              label="Open in panel"
              onClick={() => useRightPanelStore.getState().openFile(threadRef, panelPath)}
            >
              <PanelRightOpenIcon className="size-3" />
            </FigureActionButton>
          ) : null}
          <FigureActionButton
            label={isFloating ? "Close floating window" : "Float beside the chat"}
            pressed={isFloating}
            onClick={() => {
              const rect = figureRef.current?.getBoundingClientRect();
              useFloatingFigureStore.getState().toggle(
                {
                  kind: "page",
                  id: floatingId,
                  threadRef,
                  path,
                  title,
                  source: { width: rect?.width || 728, height: rect?.height || 480 },
                },
                FLOATING_FIGURE_DEFAULT_WIDTH,
              );
            }}
          >
            <PictureInPicture2Icon className="size-3" />
          </FigureActionButton>
        </span>
      ) : null}
    </figure>
  );
}

function PagePreparing({ fill, label }: { readonly fill: boolean; readonly label: string }) {
  return (
    <span
      role="status"
      data-html-page-status="preparing"
      className="relative block overflow-hidden rounded-lg"
      style={{ height: fill ? "100%" : HTML_PAGE_PLACEHOLDER_HEIGHT }}
    >
      <Skeleton className="absolute inset-0 rounded-lg" />
      <span className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
        {label}
      </span>
    </span>
  );
}

function PageNotice({
  title,
  path,
  action,
  children,
}: {
  readonly title: string;
  readonly path: string;
  readonly action?: ReactNode;
  readonly children?: ReactNode;
}) {
  return (
    <span
      role="status"
      data-html-page-status="unavailable"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground"
    >
      <span className="font-medium text-foreground/80">{title}</span>
      <code className="truncate">{path}</code>
      {children ? <span>{children}</span> : null}
      {action}
    </span>
  );
}

/**
 * The page frame. Never `allow-same-origin`: the opaque origin keeps the page
 * out of the app's session even though the server shares its origin. No
 * popups, forms or top navigation either; links reach the reader through the
 * host. The first URL is kept for the frame's life (a new src would reload the
 * page); later theme changes are posted to it.
 */
export function HtmlPageFrame({
  url,
  theme,
  title,
  fill,
  onNavigatedAway,
}: {
  readonly url: string;
  readonly theme: HtmlPageTheme;
  readonly title: string;
  readonly fill: boolean;
  readonly onNavigatedAway: () => void;
}) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [src] = useState(() => htmlPageInlineUrl(url, theme));
  const [height, setHeight] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);
  const loads = useRef(0);
  const themeRef = useRef(theme);
  useLayoutEffect(() => {
    themeRef.current = theme;
  }, [theme]);
  const postTheme = useCallback(() => {
    frameRef.current?.contentWindow?.postMessage(htmlPageThemeMessage(themeRef.current), "*");
  }, []);
  useEffect(() => {
    if (loaded) postTheme();
  }, [loaded, postTheme, theme]);

  // A page posts its height once per change, so listen from the commit that
  // inserts the frame; a passive effect could miss a fast page's first post.
  useLayoutEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const frame = frameRef.current;
      const action = readHtmlPageFrameMessage({
        data: event.data,
        source: event.source,
        frameWindow: frame?.contentWindow,
        frameFocused: frame !== null && document.activeElement === frame,
        userActive: navigator.userActivation?.isActive !== false,
      });
      if (action === null) return;
      if (action.kind === "height") {
        setHeight(action.height);
        return;
      }
      window.open(action.url, "_blank", "noopener,noreferrer");
      frame?.contentWindow?.postMessage(htmlPageResult(action.id), "*");
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  return (
    <span
      className="relative block"
      style={{ height: fill ? "100%" : (height ?? HTML_PAGE_PLACEHOLDER_HEIGHT) }}
    >
      <iframe
        ref={frameRef}
        src={src}
        title={title}
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
        loading="lazy"
        data-html-page-frame=""
        className="block size-full border-0 bg-transparent"
        style={{ colorScheme: theme.appearance }}
        onLoad={() => {
          loads.current += 1;
          // The frame is navigated once, by us. A second load means the
          // page navigated itself (a sandboxed frame always may; the app's
          // frame-src bounds where to): take it down so it gets no second try.
          if (loads.current > 1) {
            onNavigatedAway();
            return;
          }
          setLoaded(true);
        }}
      />
      {loaded || height !== null ? null : (
        <span className="pointer-events-none absolute inset-0" data-html-page-status="loading">
          <Skeleton className="absolute inset-0 rounded-lg" />
        </span>
      )}
    </span>
  );
}
